import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  type ChangeEvent,
  type ReactNode,
  type Ref,
  type TextareaHTMLAttributes,
} from 'react';

export interface TextareaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  'className' | 'size' | 'onChange'
> {
  autoResize?: boolean;
  /** Cap auto-grow at this many rows; beyond that the textarea scrolls. */
  maxRows?: number;
  /** Minimum rows when autoResize is on (controls initial height). */
  minRows?: number;
  invalid?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  ref?: Ref<HTMLTextAreaElement>;
  onValueChange?: (value: string) => void;
  /** Kept for interop with form libraries that pass the raw event. */
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
}

/** Multi-line text input.
 *  - autoResize grows height to fit content, capped by maxRows.
 *  - Trick: reset height to 'auto' before reading scrollHeight so the
 *    textarea can both grow AND shrink as content changes. */
export function Textarea(props: TextareaProps): ReactNode {
  const {
    autoResize = false,
    maxRows,
    minRows,
    invalid = false,
    size = 'md',
    className,
    ref,
    onValueChange,
    onChange,
    value,
    defaultValue,
    disabled,
    readOnly,
    rows,
    style,
    ...rest
  } = props;

  const internalRef = useRef<HTMLTextAreaElement | null>(null);

  const setRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      internalRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref !== undefined && ref !== null) {
        (ref as { current: HTMLTextAreaElement | null }).current = node;
      }
    },
    [ref],
  );

  const resize = useCallback((): void => {
    if (!autoResize) return;
    const el = internalRef.current;
    if (el === null) return;
    // Read line-height AFTER first paint so theme tokens resolve.
    const cs = window.getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.4;
    const paddingTop = parseFloat(cs.paddingTop);
    const paddingBottom = parseFloat(cs.paddingBottom);
    const borderTop = parseFloat(cs.borderTopWidth);
    const borderBottom = parseFloat(cs.borderBottomWidth);
    // Reset to auto first so scrollHeight reflects the "shrink" case too.
    el.style.height = 'auto';
    const contentHeight = el.scrollHeight - paddingTop - paddingBottom;
    const contentRows = Math.max(1, Math.round(contentHeight / lineHeight));
    const minR = minRows ?? 1;
    const cappedRows = maxRows !== undefined ? Math.min(maxRows, contentRows) : contentRows;
    const rowsToUse = Math.max(minR, cappedRows);
    const targetHeight =
      lineHeight * rowsToUse + paddingTop + paddingBottom + borderTop + borderBottom;
    el.style.height = `${String(targetHeight)}px`;
    el.style.overflowY = maxRows !== undefined && contentRows > maxRows ? 'auto' : 'hidden';
  }, [autoResize, maxRows, minRows]);

  useLayoutEffect(() => {
    resize();
  }, [resize, value, defaultValue]);

  // Recompute on window resize (font-size/line-height can shift with theme
  // swap or container width changes). Also observe the element itself so
  // CSS-driven width changes (e.g. parent flex redistribution) re-trigger.
  useEffect(() => {
    if (!autoResize) return;
    const el = internalRef.current;
    if (el === null || typeof window === 'undefined') return;
    const handler = (): void => {
      resize();
    };
    window.addEventListener('resize', handler);
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(handler);
    ro?.observe(el);
    return () => {
      window.removeEventListener('resize', handler);
      ro?.disconnect();
    };
  }, [autoResize, resize]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    onChange?.(e);
    onValueChange?.(e.target.value);
    resize();
  };

  const stateAttr =
    disabled === true ? 'disabled' : readOnly === true ? 'readonly' : invalid ? 'invalid' : 'idle';

  const classes = ['vq-textarea', className].filter(Boolean).join(' ');

  return (
    <textarea
      {...rest}
      ref={setRef}
      className={classes}
      data-component="Textarea"
      data-size={size}
      data-state={stateAttr}
      data-auto-resize={autoResize ? 'true' : undefined}
      aria-invalid={invalid || undefined}
      disabled={disabled}
      readOnly={readOnly}
      rows={rows ?? minRows}
      value={value}
      defaultValue={defaultValue}
      style={style}
      onChange={handleChange}
    />
  );
}
